import { CallDirectionType, DispositionType } from './call.type';
import { SMSDirectionType, SMSStatusType } from './sms.type';

// Contact Status Types
// Using the backend format (with underscores) as the source of truth
export const ContactStatus = {
    // Shared statuses
    NEW_LEAD: 'NEW_LEAD',
    OUTREACH: 'OUTREACH',
    UNABLE_TO_CONTACT: 'UNABLE_TO_CONTACT',

    // Brigante-specific statuses
    LOST_OPPORTUNITY_225: '$225_LOST_OPPORTUNITY',
    EXISTING_CASE_CLIENT: 'EXISTING_CASE_CLIENT',
    UNQUALIFIED: 'UNQUALIFIED',
    OPEN_CONSULT: 'OPEN_CONSULT',
    PITCHED_225_PENDING: '$225_PITCHED_PENDING',

    // Mendoza-specific statuses
    CONNECTED: 'CONNECTED',
    NEW_CALL: 'NEW_CALL',
    LOST_CONSULT_OPPORTUNITY: 'LOST_CONSULT_OPPORTUNITY',
    CLIENT: 'CLIENT',
    DISQUALIFIED: 'DISQUALIFIED',
    RETARGETING_INM: 'RETARGETING_INM',
    UNABLE_TO_CONTACT_CONTACTED: 'UNABLE_TO_CONTACT_CONTACTED',
    CONSULT_BOOKED: 'CONSULT_BOOKED',
    PRE_CLOSED: 'PRE_CLOSED',
    AWAITING_DOCS: 'AWAITING_DOCS',
    DISQUALIFIED_VISAT: 'DISQUALIFIED_VISAT',
    WRONG_NUMBER_BAD_NUMBER: 'WRONG_NUMBER_BAD_NUMBER',
    NO_TCPA: 'NO_TCPA',
} as const;

export type ContactStatusType = (typeof ContactStatus)[keyof typeof ContactStatus];

// Priority levels
export const ContactPriority = {
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
} as const;

export type ContactPriorityType = (typeof ContactPriority)[keyof typeof ContactPriority];

// Core Contact Interface
export interface Contact {
    contactId: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    createdAt: string; // ISO date string
    source: string;
    status: ContactStatusType | string; // Allow string for dynamic statuses from backend
    crmId: string;
    crmStatus?: string; // Optional CRM-specific status
    priority?: ContactPriorityType;
    workflowIds: string[];
    partyStage?: string; // Backend field name for status
}

// Workflow Interface
export interface Workflow {
    workflowId: string;
    workflowName: string;
}

// Pagination Interface
export interface Pagination {
    page: number;
    limit: number;
    totalContacts: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

// Call History Interface
export interface CallHistoryItem {
    id: string;
    tenant_id?: string;
    workflow_id: string;
    workflow_name: string;
    party_id?: string;
    created_at: string;
    updated_at?: string;
    status: string;
    contact_id?: string;
    scheduled_call_date?: string;
    executed_call_date?: string;
    call_source?: string;
    call_direction?: CallDirectionType;
    current_node_id?: string;
    start_time?: string;
    end_time?: string;
    ai_started_time?: string;
    ai_ended_time?: string;
    ai_call_duration?: number;
    target_number?: string;
    disposition?: DispositionType;
    aggregatedTagsUsed?: boolean;
    attempts?: number;
    node_outcome_id?: string;
    call_duration?: number;
    edges?: {
        workflow?: {
            id: string;
            title: string;
        };
    };
}

// Status History Interface
export interface StatusHistoryItem {
    id: string;
    created_at: string;
    party_stage: string;
    workflow_id: string;
    workflow_name: string;
    changed_by?: string;
    note?: string;
}

// API Request Types
export interface GetContactsParams {
    page?: number;
    limit?: number;
    query?: string;
    /** Single status, multiple statuses (OR), or omit / "All Statuses" for no filter */
    status?: string | string[];
    workflowId?: string;
    sortBy?: 'newest' | 'oldest' | 'name';
}

export interface GetContactsResponse {
    contacts: Contact[];
    workflows: Workflow[];
    statuses: string[]; // Raw statuses from backend
    pagination: Pagination;
}

export interface UploadContactsRequest {
    contacts: Array<{
        firstName: string;
        lastName: string;
        phoneNumber: string;
        crmId?: string;
        crmStatus?: string;
        priority?: ContactPriorityType;
    }>;
    workflowId: string;
}

export interface UploadContactsResponse {
    success: boolean;
    message?: string;
    invalidPhones?: string[];
    duplicatePhones?: string[];
    contactsAdded?: number;
}

export interface DeleteContactsRequest {
    contactIds: string[];
}

export interface DeleteContactsResponse {
    success: boolean;
    message?: string;
    deletedCount?: number;
}

export interface UpdateContactStatusRequest {
    status: ContactStatusType | string;
    note: string;
}

export interface UpdateContactStatusResponse {
    success: boolean;
    message?: string;
    contact?: Contact;
}

export interface GetContactDetailsResponse extends Contact {
    calls?: CallHistoryItem[];
    history?: StatusHistoryItem[];
    sms?: SMSHistoryItem[];
}

export interface SMSHistoryItem {
    smsId: string;
    sentTime: string;
    scheduledDate: string;
    contactName: string;
    phoneNumber: string;
    messageContent: string;
    status: SMSStatusType;
    disposition: DispositionType;
    contactId: string;
    crmId: string;
    overDue: boolean;
    deliveredTime: string;
    messageDirection: SMSDirectionType;
    workflowId: string;
    workflowName: string;
}

// API Error Response
export interface APIErrorResponse {
    error: string;
    message?: string;
    details?: string;
    statusCode?: number;
}

// Generic API Response Wrapper (optional, if you want to standardize)
export type APIResponse<T> = { success: true; data: T } | { success: false; error: APIErrorResponse };

// Helper function to format status for display (NEW_LEAD -> New Lead)
export const formatStatusForDisplay = (status: string): string => {
    return status
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
};

// Helper function to format status for backend
export const formatStatusForBackend = (status: string): string => {
    return status.replace(/ /g, '_').toUpperCase();
};
